import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  Video, VideoOff, Mic, MicOff, Phone, Monitor, MessageSquare,
  Users, Settings, ArrowLeft, Loader2, X, Send, Maximize2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const VideoRoom = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const [callFrame, setCallFrame] = useState(null);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [participants, setParticipants] = useState([]);
  
  const videoContainerRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    joinSession();
    return () => {
      if (callFrame) {
        callFrame.leave();
        callFrame.destroy();
      }
    };
  }, [sessionId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const joinSession = async () => {
    try {
      const response = await api.post(`/tutoring/sessions/${sessionId}/join`);
      setSessionData(response.data);
      
      // Initialize Daily.co
      await initializeDaily(response.data);
    } catch (error) {
      console.error('Error joining session:', error);
      toast.error('Failed to join session');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const initializeDaily = async (data) => {
    // Check if Daily.co script is loaded
    if (!window.DailyIframe) {
      // Load Daily.co script dynamically
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/@daily-co/daily-js';
      script.async = true;
      script.onload = () => createCallFrame(data);
      document.body.appendChild(script);
    } else {
      createCallFrame(data);
    }
  };

  const createCallFrame = async (data) => {
    try {
      const frame = window.DailyIframe.createFrame(videoContainerRef.current, {
        iframeStyle: {
          width: '100%',
          height: '100%',
          border: '0',
          borderRadius: '12px'
        },
        showLeaveButton: false,
        showFullscreenButton: true
      });

      // Set up event listeners
      frame.on('joined-meeting', handleJoinedMeeting);
      frame.on('left-meeting', handleLeftMeeting);
      frame.on('participant-joined', handleParticipantJoined);
      frame.on('participant-left', handleParticipantLeft);
      frame.on('app-message', handleAppMessage);
      frame.on('error', handleError);

      // Join the room
      await frame.join({
        url: data.room_url,
        token: data.token,
        userName: data.user_name
      });

      setCallFrame(frame);
    } catch (error) {
      console.error('Error creating call frame:', error);
      toast.error('Failed to connect to video call');
    }
  };

  const handleJoinedMeeting = (event) => {
    toast.success('Connected to session!');
    updateParticipants(event.participants);
  };

  const handleLeftMeeting = () => {
    toast.info('Left the session');
  };

  const handleParticipantJoined = (event) => {
    toast.info(`${event.participant.user_name} joined`);
    if (callFrame) {
      updateParticipants(callFrame.participants());
    }
  };

  const handleParticipantLeft = (event) => {
    toast.info(`${event.participant.user_name} left`);
    if (callFrame) {
      updateParticipants(callFrame.participants());
    }
  };

  const handleAppMessage = (event) => {
    if (event.data?.type === 'chat') {
      setMessages(prev => [...prev, {
        id: Date.now(),
        sender: event.data.sender,
        text: event.data.text,
        timestamp: new Date().toLocaleTimeString()
      }]);
    }
  };

  const handleError = (error) => {
    console.error('Daily error:', error);
    toast.error('Connection error occurred');
  };

  const updateParticipants = (participantsObj) => {
    const participantList = Object.values(participantsObj || {}).map(p => ({
      id: p.session_id,
      name: p.user_name || 'Guest',
      isLocal: p.local,
      videoOn: p.video,
      audioOn: p.audio
    }));
    setParticipants(participantList);
  };

  const toggleVideo = () => {
    if (callFrame) {
      callFrame.setLocalVideo(!isVideoOn);
      setIsVideoOn(!isVideoOn);
    }
  };

  const toggleAudio = () => {
    if (callFrame) {
      callFrame.setLocalAudio(!isAudioOn);
      setIsAudioOn(!isAudioOn);
    }
  };

  const toggleScreenShare = async () => {
    if (callFrame) {
      if (isScreenSharing) {
        await callFrame.stopScreenShare();
      } else {
        await callFrame.startScreenShare();
      }
      setIsScreenSharing(!isScreenSharing);
    }
  };

  const sendChatMessage = () => {
    if (!newMessage.trim() || !callFrame) return;

    const messageData = {
      type: 'chat',
      sender: user?.first_name || 'You',
      text: newMessage
    };

    // Send to all participants
    callFrame.sendAppMessage(messageData, '*');

    // Add to local messages
    setMessages(prev => [...prev, {
      id: Date.now(),
      sender: 'You',
      text: newMessage,
      timestamp: new Date().toLocaleTimeString(),
      isLocal: true
    }]);

    setNewMessage('');
  };

  const leaveSession = async () => {
    if (callFrame) {
      await callFrame.leave();
      callFrame.destroy();
    }
    
    // End session if tutor
    if (sessionData?.is_host) {
      try {
        await api.post(`/tutoring/sessions/${sessionId}/end`);
      } catch (error) {
        console.error('Error ending session:', error);
      }
    }
    
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
          <p className="text-lg">Connecting to session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-900 flex flex-col">
      {/* Header */}
      <div className="bg-slate-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={leaveSession} className="text-white hover:text-red-400">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-white font-bold">{sessionData?.room_name || 'Tutoring Session'}</h1>
            <p className="text-slate-400 text-sm">{participants.length} participant(s)</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsChatOpen(!isChatOpen)}
            className="text-white hover:bg-slate-700"
          >
            <MessageSquare className="w-5 h-5" />
            {messages.length > 0 && (
              <span className="ml-1 bg-red-500 text-white text-xs px-1.5 rounded-full">
                {messages.length}
              </span>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-slate-700"
          >
            <Users className="w-5 h-5" />
            <span className="ml-1">{participants.length}</span>
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Video Area */}
        <div className="flex-1 p-4">
          <div 
            ref={videoContainerRef} 
            className="w-full h-full bg-slate-800 rounded-xl overflow-hidden"
          />
        </div>

        {/* Chat Sidebar */}
        {isChatOpen && (
          <div className="w-80 bg-slate-800 flex flex-col border-l border-slate-700">
            <div className="p-3 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-white font-semibold">Chat</h3>
              <button onClick={() => setIsChatOpen(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {messages.map(msg => (
                <div key={msg.id} className={`${msg.isLocal ? 'text-right' : ''}`}>
                  <div className={`inline-block max-w-[80%] rounded-lg px-3 py-2 ${
                    msg.isLocal ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-white'
                  }`}>
                    {!msg.isLocal && (
                      <p className="text-xs text-emerald-400 mb-1">{msg.sender}</p>
                    )}
                    <p className="text-sm">{msg.text}</p>
                    <p className="text-xs text-slate-400 mt-1">{msg.timestamp}</p>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            
            <div className="p-3 border-t border-slate-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                  placeholder="Type a message..."
                  className="flex-1 bg-slate-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <Button onClick={sendChatMessage} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Control Bar */}
      <div className="bg-slate-800 px-4 py-4 flex items-center justify-center gap-4">
        <Button
          onClick={toggleAudio}
          variant={isAudioOn ? "secondary" : "destructive"}
          size="lg"
          className="rounded-full w-14 h-14"
        >
          {isAudioOn ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
        </Button>
        
        <Button
          onClick={toggleVideo}
          variant={isVideoOn ? "secondary" : "destructive"}
          size="lg"
          className="rounded-full w-14 h-14"
        >
          {isVideoOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
        </Button>
        
        <Button
          onClick={toggleScreenShare}
          variant={isScreenSharing ? "default" : "secondary"}
          size="lg"
          className={`rounded-full w-14 h-14 ${isScreenSharing ? 'bg-emerald-600' : ''}`}
        >
          <Monitor className="w-6 h-6" />
        </Button>
        
        <Button
          onClick={leaveSession}
          variant="destructive"
          size="lg"
          className="rounded-full w-14 h-14 bg-red-600 hover:bg-red-700"
        >
          <Phone className="w-6 h-6 rotate-[135deg]" />
        </Button>
      </div>
    </div>
  );
};

export default VideoRoom;
